import { useToast } from '@/components/ui/toast'
import {getAgencesID, getStoredEndDate, getStoredStartDate} from '../lib/localStorage'
import type {
  GetUserAgencesViewModel,
  PaginationOffsetResponseModel_CardDetailPropositionViewModel_,
  RangeColor
} from '@/lib/types'

export class ApiError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

class BaseRepository {
  private toast = useToast().toast

  async buildUrl(endpoint: string, params: Record<string, string> = {}): Promise<string> {
    const query = await this.buildQueryParams(params)
    const separator = endpoint.includes('?') ? '&' : '?'
    return `${endpoint}${separator}${query}`
  }

  async fetchData<T>(url: string, options?: RequestInit): Promise<T> {
    try {
      const response = await fetch(url, options)
      if (!response.ok) {
        return Promise.reject(new ApiError(`La requête a échoué avec le statut ${response.status}`))
      }
      return response.json()
    } catch (error) {
      if (error instanceof ApiError) {
        console.error('API Error:', error.message)
        return Promise.reject(error)
      }

      // Create and return a rejected promise rather than throwing
      return Promise.reject(
        new ApiError('Une erreur est survenue lors de la communication avec le serveur'),
      )
    }
  }

  async saveSettings<Settings extends Record<string, RangeColor[]>>(
    url: string,
    settings: Settings,
  ): Promise<void> {
    try {
      await this.fetchData<void>(await this.buildUrl(url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      })
      this.toast({
        title: 'Succès',
        description: 'Les paramètres ont bien été sauvegardés',
      })
    } catch (error) {
      this.toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors de la sauvegarde de vos paramètres',
        variant: 'destructive',
      })
      console.error('Error saving settings:', error)
    }
  }

  async downloadExcelFile(endpoint: string, defaultFilename: string): Promise<void> {
    try {
      const url = await this.buildUrl(endpoint)
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        return Promise.reject(
          new ApiError(`Le téléchargement a échoué avec le statut ${response.status}`),
        )
      }

      const disposition = response.headers.get('Content-Disposition')
      const filename = disposition
        ? disposition.split('filename=')[1].replace(/"/g, '')
        : defaultFilename

      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
      this.toast({
        title: 'Erreur',
        description: 'Une erreur est survenue lors du téléchargement du fichier',
        variant: 'destructive',
      })
      console.error('Error downloading file:', error)
    }
  }

  async getAgences(): Promise<GetUserAgencesViewModel[]> {
    try {
      const response = await fetch('api/users/agences')
      if (!response.ok) {
        return Promise.reject(new ApiError('Network response was not ok'))
      }
      return await response.json()
    } catch (error) {
      console.error('Error fetching agences:', error)
      if (error instanceof ApiError) {
        return Promise.reject(error)
      }
      return Promise.reject(new ApiError('Error fetching agences'))
    }
  }

  private async buildQueryParams(
    additionalParams: Record<string, string> = {},
  ): Promise<URLSearchParams> {
    const query = new URLSearchParams(additionalParams)
    const agences_id = await getAgencesID()
    agences_id.forEach((id: number) => query.append('agences_ids', id.toString()))
    return query
  }

  async getPropositionsTableData(
    offset: number,
    limit: number,
    order_by: string = '',
    order_direction: 'asc' | 'desc' = 'asc',
  ): Promise<PaginationOffsetResponseModel_CardDetailPropositionViewModel_> {
    const params = {
      offset: offset.toString(),
      limit: limit.toString(),
      order_by,
      order_direction,
    }
    const url = await this.buildUrl(
      `api/widgets/card-detail-propositions/?date_debut=${getStoredStartDate()}&date_fin=${getStoredEndDate()}`,
      params,
    )
    return this.fetchData<PaginationOffsetResponseModel_CardDetailPropositionViewModel_>(
      url,
    )
  }

    downloadPropositionExcel = async (): Promise<void> => {
    const params = {
        date_debut: getStoredStartDate(),
        date_fin: getStoredEndDate(),
    }

    const url = await this.buildUrl('api/widgets/card-detail-propositions/excel', params)
    await this.downloadExcelFile(url, `propositions_${getStoredEndDate()}.xlsx`)
    }
}

export default new BaseRepository()
